import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import styled from 'styled-components';
import { VirtualRoom } from '../services/VirtualRoomStorage';
import { ColorPicker } from './ColorPicker';
import { KindPicker } from './KindPicker';
import { useInputLocking } from '../../../../../../hooks/useInputLocking';
import { InputLockReason } from '../../../../../../services/InputController';

// Styled components matching Map Editor design
const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: rgba(12, 12, 12, 0.98) !important;
    border: 1px solid rgba(255, 255, 255, 0.06) !important;
    border-radius: 16px !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 
      0 0 60px rgba(0, 0, 0, 0.4),
      0 0 12px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.03) !important;
    color: rgba(255, 255, 255, 0.95) !important;
    max-width: 480px !important;
    width: 100% !important;
  }
`;

const DialogHeader = styled(DialogTitle)`
  padding: 24px 24px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
  background: linear-gradient(
    135deg,
    rgba(20, 20, 20, 0.9) 0%,
    rgba(16, 16, 16, 0.95) 100%
  ) !important;
  position: relative !important;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.08) 50%,
      transparent 100%
    );
  }
`;

const DialogBody = styled(DialogContent)`
  padding: 24px !important;
  background: rgba(0, 0, 0, 0.02) !important;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    transition: background 0.2s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const DialogFooter = styled(DialogActions)`
  padding: 16px 24px 24px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
  background: rgba(12, 12, 12, 0.8) !important;
  gap: 12px !important;
`;

const SectionTitle = styled(Typography)`
  font-size: 12px !important;
  font-weight: 600 !important;
  margin-bottom: 16px !important;
  color: rgba(255, 255, 255, 0.7) !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.03) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 12px !important;
    color: rgba(255, 255, 255, 0.95) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    
    &:hover {
      background: rgba(255, 255, 255, 0.05) !important;
      border-color: rgba(255, 255, 255, 0.12) !important;
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 0.08) !important;
      border-color: #f97415 !important;
      box-shadow: 0 0 0 3px rgba(249, 116, 21, 0.1) !important;
    }
    
    &.Mui-error {
      border-color: rgba(231, 76, 60, 0.6) !important;
      box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.2) !important;
    }
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.6) !important;
    font-weight: 500 !important;
    
    &.Mui-focused {
      color: #f97415 !important;
    }
    
    &.Mui-error {
      color: rgba(231, 76, 60, 0.9) !important;
    }
  }
  
  .MuiFormHelperText-root {
    color: rgba(255, 255, 255, 0.5) !important;
    font-size: 0.75rem !important;
    
    &.Mui-error {
      color: rgba(231, 76, 60, 0.9) !important;
    }
  }
`;

const PreviewCard = styled(Card)`
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
  }
`;

const ActionButton = styled(Button)<{ $primary?: boolean }>`
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 10px 24px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #f97415 0%, #ffa047 100%) !important;
    color: white !important;
    border: 1px solid rgba(249, 116, 21, 0.4) !important;
    
    &:hover {
      background: linear-gradient(135deg, #e86a0f 0%, #f2943a 100%) !important;
      transform: translateY(-2px) !important;
      box-shadow:
        0 4px 14px rgba(249, 116, 21, 0.3),
        0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }
    
    &:disabled {
      background: rgba(255, 255, 255, 0.1) !important;
      color: rgba(255, 255, 255, 0.3) !important;
      transform: none !important;
      box-shadow: none !important;
    }
  ` : `
    background: rgba(255, 255, 255, 0.05) !important;
    color: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    
    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      color: rgba(255, 255, 255, 0.95) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      transform: translateY(-1px) !important;
    }
  `}
`;

const CloseButton = styled(IconButton)`
  color: rgba(255, 255, 255, 0.7) !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 12px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: 1px solid transparent !important;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.08) !important;
    color: rgba(255, 255, 255, 0.95) !important;
    transform: scale(1.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
`;

interface RoomCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onRoomCreated: (room: VirtualRoom) => void;
  onRoomUpdated: (room: VirtualRoom) => void;
  editingRoom?: VirtualRoom | null;
  isSubmitting?: boolean;
}

export const RoomCreationDialog: React.FC<RoomCreationDialogProps> = ({
  open,
  onClose,
  onRoomCreated,
  onRoomUpdated,
  editingRoom,
  isSubmitting = false
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3f51b5');
  const [uniqueRoomId, setUniqueRoomId] = useState('');
  const [proximityDistance, setProximityDistance] = useState(5);
  const [kind, setKind] = useState<VirtualRoom['kind']>('meeting');
  const [errors, setErrors] = useState<{ name?: string; color?: string; uniqueRoomId?: string }>({});

  // Input locking for text fields
  const {
    inputFocused,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleKeyUp,
    KeyboardCaptureLayer
  } = useInputLocking({
    lockReason: InputLockReason.MAP_EDITOR,
    debugLabel: 'RoomCreationDialog',
    enableDebugLogs: false
  });

  // Reset form when dialog opens/closes or editing room changes
  useEffect(() => {
    if (open) {
      if (editingRoom) {
        setName(editingRoom.name);
        setColor(editingRoom.color);
        setUniqueRoomId(editingRoom.properties?.uniqueRoomId || '');
        setProximityDistance(editingRoom.properties?.proximityDistance || 5);
        setKind(editingRoom.kind || 'meeting');
      } else {
        setName('');
        setColor('#3f51b5');
        setUniqueRoomId('');
        setProximityDistance(5);
        setKind('meeting');
      }
      setErrors({});
    }
  }, [open, editingRoom]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; color?: string; uniqueRoomId?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Room name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Room name must be at least 2 characters';
    } else if (name.trim().length > 50) {
      newErrors.name = 'Room name must be less than 50 characters';
    }

    if (!uniqueRoomId.trim()) {
      newErrors.uniqueRoomId = 'Unique Room ID is required';
    } else if (uniqueRoomId.trim().length < 2) {
      newErrors.uniqueRoomId = 'Unique Room ID must be at least 2 characters';
    } else if (uniqueRoomId.trim().length > 30) {
      newErrors.uniqueRoomId = 'Unique Room ID must be less than 30 characters';
    } else if (uniqueRoomId.includes(' ')) {
      newErrors.uniqueRoomId = 'Unique Room ID cannot contain spaces';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(uniqueRoomId.trim())) {
      newErrors.uniqueRoomId = 'Unique Room ID can only contain letters, numbers, hyphens, and underscores (no spaces)';
    }

    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      newErrors.color = 'Please select a valid color';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateField = (field: 'name' | 'uniqueRoomId' | 'color'): string | undefined => {
    switch (field) {
      case 'name':
        if (!name.trim()) {
          return 'Room name is required';
        } else if (name.trim().length < 2) {
          return 'Room name must be at least 2 characters';
        } else if (name.trim().length > 50) {
          return 'Room name must be less than 50 characters';
        }
        return undefined;
        
      case 'uniqueRoomId':
        if (!uniqueRoomId.trim()) {
          return 'Unique Room ID is required';
        } else if (uniqueRoomId.trim().length < 2) {
          return 'Unique Room ID must be at least 2 characters';
        } else if (uniqueRoomId.trim().length > 30) {
          return 'Unique Room ID must be less than 30 characters';
        } else if (uniqueRoomId.includes(' ')) {
          return 'Unique Room ID cannot contain spaces';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(uniqueRoomId.trim())) {
          return 'Unique Room ID can only contain letters, numbers, hyphens, and underscores (no spaces)';
        }
        return undefined;
        
      case 'color':
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
          return 'Please select a valid color';
        }
        return undefined;
        
      default:
        return undefined;
    }
  };

  const updateFieldError = (field: 'name' | 'uniqueRoomId' | 'color') => {
    const error = validateField(field);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const roomData = {
      name: name.trim(),
      color,
      kind,
      properties: {
        uniqueRoomId: uniqueRoomId.trim(),
        proximityDistance
      }
    };

    try {
      if (editingRoom) {
        const updatedRoom = { ...editingRoom, ...roomData };
        onRoomUpdated(updatedRoom);
      } else {
        const newRoom = {
          ...roomData,
          id: `room_${Date.now()}_${Math.random().toString(36).substr(2, 9).replace(/[.]/g, '_')}`,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          isActive: false,
        } as VirtualRoom;
        onRoomCreated(newRoom);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save room:', error);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <>
      {/* Keyboard capture layer when input is focused */}
      {inputFocused && <KeyboardCaptureLayer />}
      
      <StyledDialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm" 
        fullWidth
      >
        <DialogHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HomeIcon sx={{ color: '#f97415', fontSize: 24 }} />
              <Typography variant="h6" sx={{ 
                color: 'rgba(255, 255, 255, 0.95)', 
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em'
              }}>
                {editingRoom ? 'Edit Room' : 'Create New Room'}
              </Typography>
              {editingRoom && (
                <Chip 
                  label="Editing" 
                  size="small" 
                  sx={{ 
                    background: 'rgba(249, 116, 21, 0.2)',
                    color: '#f97415',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }} 
                />
              )}
            </Box>
            <CloseButton
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <CloseIcon />
            </CloseButton>
          </Box>
        </DialogHeader>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information Section */}
            <Box>
              <SectionTitle>
                <HomeIcon sx={{ fontSize: 16, color: '#ffa047' }} />
                Basic Information
              </SectionTitle>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StyledTextField
                  autoFocus
                  label="Room Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    updateFieldError('name');
                  }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                  onKeyPress={handleKeyPress}
                  error={!!errors.name}
                  helperText={errors.name || 'Enter a descriptive name for your room'}
                  disabled={isSubmitting}
                  fullWidth
                  size="small"
                />

                <ColorPicker
                  value={color}
                  onChange={(newColor) => {
                    setColor(newColor);
                    updateFieldError('color');
                  }}
                  label="Room Color"
                />

                {errors.color && (
                  <Alert severity="error" sx={{ 
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    color: 'rgba(244, 67, 54, 0.9)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    borderRadius: 2
                  }}>
                    {errors.color}
                  </Alert>
                )}
              </Box>
            </Box>

            {/* Preview Section */}
            {name.trim() && (
              <Box>
                <SectionTitle>
                  <CheckIcon sx={{ fontSize: 16, color: 'rgba(76, 175, 80, 0.8)' }} />
                  Preview
                </SectionTitle>
                
                <PreviewCard>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color,
                          border: '2px solid',
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontWeight: 600
                        }}>
                          {name.trim()}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.7rem'
                        }}>
                          Room Preview
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </PreviewCard>
              </Box>
            )}

            {/* Room Properties Section */}
            <Box>
              <SectionTitle>
                <SettingsIcon sx={{ fontSize: 16, color: 'rgba(255, 193, 7, 0.8)' }} />
                Room Properties
              </SectionTitle>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StyledTextField
                  label="Unique Room ID"
                  value={uniqueRoomId}
                  onChange={(e) => {
                    setUniqueRoomId(e.target.value);
                    updateFieldError('uniqueRoomId');
                  }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.uniqueRoomId}
                  helperText={errors.uniqueRoomId || 'A unique identifier for this room (letters, numbers, hyphens, underscores - no spaces)'}
                  size="small"
                />

                <StyledTextField
                  label="Proximity Distance"
                  type="number"
                  value={proximityDistance}
                  onChange={(e) => setProximityDistance(Number(e.target.value))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                  fullWidth
                  required
                  disabled={isSubmitting}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Distance in tiles for proximity detection (1-100)"
                  size="small"
                />

                <KindPicker value={kind} onChange={setKind} disabled={isSubmitting} />
              </Box>
            </Box>
          </Box>
        </DialogBody>

        <DialogFooter>
          <ActionButton 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButton>
          <ActionButton 
            onClick={handleSubmit}
            disabled={!name.trim() || !uniqueRoomId.trim() || isSubmitting || !!errors.name || !!errors.color || !!errors.uniqueRoomId}
            $primary
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            {isSubmitting ? 'Saving...' : (editingRoom ? 'Update Room' : 'Create Room')}
          </ActionButton>
        </DialogFooter>
      </StyledDialog>
    </>
  );
}; 